import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {shuffle} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import DrawPromptCard from '../common/DrawPromptCard';
import CollectAnswers, {CollectedAnswer} from '../common/CollectAnswers';
import VoteAmongOptions, {VoteOption, VoteRecord} from '../common/VoteAmongOptions';
import {BASE_PROMPTS} from './captionDrawPrompts';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_PROMPTS_KEY = 'captiondraw_custom_prompts';

type Stage = 'setup' | 'artistReady' | 'drawing' | 'collecting' | 'voting' | 'result';

const CaptionDrawGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customPrompts, setCustomPrompts] = useState<string[]>(() => loadJSON(CUSTOM_PROMPTS_KEY, []));
    const [newPrompt, setNewPrompt] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [artistIndex, setArtistIndex] = useState(0);
    const [currentPrompt, setCurrentPrompt] = useState('');
    const [usedPrompts, setUsedPrompts] = useState<Set<string>>(new Set());
    const [drawingDataUrl, setDrawingDataUrl] = useState('');
    const [options, setOptions] = useState<VoteOption[]>([]);
    const [votes, setVotes] = useState<VoteRecord[]>([]);
    const [scores, setScores] = useState<Record<string, number>>({});
    const [roundPoints, setRoundPoints] = useState<Record<string, number>>({});

    const allPrompts = useMemo(() => [...BASE_PROMPTS, ...customPrompts], [customPrompts]);

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const addCustomPrompt = () => {
        const text = newPrompt.trim();
        if (!text) return;
        const next = [...customPrompts, text];
        setCustomPrompts(next);
        saveJSON(CUSTOM_PROMPTS_KEY, next);
        setNewPrompt('');
    };

    const removeCustomPrompt = (text: string) => {
        const next = customPrompts.filter(p => p !== text);
        setCustomPrompts(next);
        saveJSON(CUSTOM_PROMPTS_KEY, next);
    };

    const pickPrompt = (pool: string[], used: Set<string>): {prompt: string; nextUsed: Set<string>} => {
        let available = pool.filter(p => !used.has(p));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const prompt = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(prompt);
        return {prompt, nextUsed: updatedUsed};
    };

    const beginRound = (nextArtistIndex: number, pool: string[], used: Set<string>) => {
        const {prompt, nextUsed} = pickPrompt(pool, used);
        setArtistIndex(nextArtistIndex);
        setCurrentPrompt(prompt);
        setUsedPrompts(nextUsed);
        setDrawingDataUrl('');
        setOptions([]);
        setVotes([]);
        setRoundPoints({});
        setStage('artistReady');
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || allPrompts.length === 0) return;
        setScores(Object.fromEntries(players.map(p => [p.id, 0])));
        beginRound(0, allPrompts, new Set());
    };

    const nextRound = () => beginRound((artistIndex + 1) % players.length, allPrompts, usedPrompts);

    const backToSetup = () => {
        setStage('setup');
        setCurrentPrompt('');
    };

    const artist = players[artistIndex];
    const guessers = useMemo(
        () => players.filter((_, i) => i !== artistIndex),
        [players, artistIndex]
    );

    const handleCollectDone = (answers: CollectedAnswer[]) => {
        const opts: VoteOption[] = shuffle([
            {id: 'truth', text: currentPrompt, authorId: null},
            ...answers.map((a): VoteOption => ({id: a.playerId, text: a.text, authorId: a.playerId})),
        ]);
        setOptions(opts);
        setStage('voting');
    };

    const handleVoteDone = (voteRecords: VoteRecord[]) => {
        setVotes(voteRecords);
        const points: Record<string, number> = {};
        voteRecords.forEach(v => {
            const opt = options.find(o => o.id === v.optionId);
            if (!opt) return;
            if (opt.authorId === null) {
                points[v.playerId] = (points[v.playerId] || 0) + 1;
                if (artist) points[artist.id] = (points[artist.id] || 0) + 1;
            } else {
                points[opt.authorId] = (points[opt.authorId] || 0) + 1;
            }
        });
        setRoundPoints(points);
        setScores(s => {
            const next = {...s};
            Object.entries(points).forEach(([pid, pts]) => {
                next[pid] = (next[pid] || 0) + pts;
            });
            return next;
        });
        setStage('result');
    };

    const ranking = useMemo(
        () => [...players].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)),
        [players, scores]
    );

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Подпиши рисунок</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Художник втайне получает фразу и рисует её. Остальные придумывают свои варианты
                            подписи к рисунку — настоящая фраза подмешивается к ним. Все голосуют, где правда.
                            Очко за угаданную правду, художнику — за каждого, кто угадал, остальным — за каждого,
                            кого обманул их вариант.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Фразы для рисования ({allPrompts.length})</h3>

                            {customPrompts.length > 0 && (
                                <div className="GmList" style={{marginBottom: 12}}>
                                    {customPrompts.map(p => (
                                        <div className="GmListItem" key={p}>
                                            <span className="GmListItem-name">{p}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomPrompt(p)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow">
                                <input
                                    className="GmInput"
                                    placeholder="Своя фраза для рисования"
                                    value={newPrompt}
                                    onChange={(e) => setNewPrompt(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomPrompt();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomPrompt}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'artistReady' && artist && (
                    <div className="GmPassCard">
                        <p className="GmPassCard-hint">Художник в этом раунде</p>
                        <h2 className="GmPassCard-name">{artist.name}</h2>
                        <p className="GmHint">Передайте телефон {artist.name} — остальные не должны видеть фразу</p>
                        <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('drawing')}>
                            Я — {artist.name}, начать рисовать
                        </button>
                    </div>
                )}

                {stage === 'drawing' && artist && (
                    <DrawPromptCard
                        label={`${artist.name} рисует`}
                        prompt={currentPrompt}
                        warning="⚠ Только художник смотрит на фразу — остальные не должны её видеть!"
                        submitLabel="Готово, показать остальным"
                        onSubmit={(dataUrl) => {
                            setDrawingDataUrl(dataUrl);
                            setStage('collecting');
                        }}
                    />
                )}

                {stage === 'collecting' && (
                    <CollectAnswers
                        players={guessers}
                        placeholder="Твоя версия подписи"
                        prompt={
                            <>
                                <img className="GmTelephoneChain-image" src={drawingDataUrl} alt=""/>
                                <p className="GmHint" style={{marginTop: 10}}>
                                    Придумай смешную подпись к этому рисунку — как будто это и есть загаданная фраза
                                </p>
                            </>
                        }
                        onDone={handleCollectDone}
                    />
                )}

                {stage === 'voting' && (
                    <VoteAmongOptions
                        players={guessers}
                        options={options}
                        voteQuestion="Что из этого — настоящая фраза художника?"
                        prompt={<img className="GmTelephoneChain-image" src={drawingDataUrl} alt=""/>}
                        onDone={handleVoteDone}
                    />
                )}

                {stage === 'result' && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Что это было</h3>
                        <img className="GmTelephoneChain-image" src={drawingDataUrl} alt="" style={{marginBottom: 12}}/>
                        <p className="GmHint">Загаданная фраза</p>
                        <p className="GmRoleLocation">{currentPrompt}</p>

                        <div className="GmRevealList" style={{marginTop: 12}}>
                            {options.map(o => {
                                const voters = votes
                                    .filter(v => v.optionId === o.id)
                                    .map(v => players.find(p => p.id === v.playerId)?.name)
                                    .filter(Boolean);
                                const authorName = o.authorId === null ? 'Правда' : (players.find(p => p.id === o.authorId)?.name || '?');
                                return (
                                    <div className={`GmRevealItem ${o.authorId === null ? 'GmRevealItem--flagged' : ''}`} key={o.id} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <div><strong>{o.text}</strong> — {authorName}</div>
                                        {voters.length > 0 && <div className="GmHint">Голосовали: {voters.join(', ')}</div>}
                                    </div>
                                );
                            })}
                        </div>

                        <h3 className="GmCard-title" style={{marginTop: 16}}>Счёт</h3>
                        <div className="GmRevealList">
                            {ranking.map(p => (
                                <div className="GmRevealItem" key={p.id}>
                                    {p.name} — {scores[p.id] || 0}{roundPoints[p.id] ? ` (+${roundPoints[p.id]})` : ''}
                                </div>
                            ))}
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRound}>Следующий раунд</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CaptionDrawGame;
